import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { clearOutcomeCache } from '@/lib/measure-outcomes';
import { MipsDataService } from '@/lib/mips-data-service';
import { useAuth } from './AuthContext';

interface ProgramYearContextType {
  /** The year the user is VIEWING. Drives every data screen. Defaults to the operating year. */
  programYear: number;
  /** The global system "operating" year (admin-set). New uploads default here; it's the default view. */
  operatingYear: number;
  /** Years selectable in the Viewing Year dropdown (years that have data + the operating year). */
  availableYears: number[];
  loading: boolean;
  /** Switch the per-user viewing year (top-bar dropdown). Does not persist across sessions. */
  setViewingYear: (year: number) => void;
  /** Admin: set the global operating year. Also resets the viewing year to it. */
  updateProgramYear: (year: number) => Promise<void>;
  refreshProgramYear: () => Promise<void>;
  lockedYears: number[];
  isYearLocked: (year: number) => boolean;
  /** Is the year currently being VIEWED locked (read-only)? */
  isCurrentYearLocked: boolean;
  refreshLockedYears: () => Promise<void>;
}

const ProgramYearContext = createContext<ProgramYearContextType | undefined>(undefined);

interface ProgramYearProviderProps {
  children: ReactNode;
}

export function ProgramYearProvider({ children }: ProgramYearProviderProps) {
  const fallbackYear = new Date().getFullYear();
  // operatingYear = global system year; programYear (viewingYear) = what the user is looking at.
  const [operatingYear, setOperatingYear] = useState<number>(fallbackYear);
  const [programYear, setProgramYearState] = useState<number>(fallbackYear);
  const [availableYears, setAvailableYears] = useState<number[]>([fallbackYear]);
  const [loading, setLoading] = useState(true);
  const [lockedYears, setLockedYears] = useState<number[]>([]);

  // ProgramYearProvider mounts above the login gate, so its initial mount can
  // happen before the user is authenticated. React to the auth token so the
  // year data (operating year + available years + locked years) is (re)loaded
  // the moment the user logs in — not just once at mount.
  const { token } = useAuth();

  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
  const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` });

  // Switch the per-user viewing year (does not persist; clears year-dependent caches).
  // Sync the static-data service SYNCHRONOUSLY so any child that re-reads on the
  // year change sees the correct year's data (no render-order race).
  const setViewingYear = (year: number) => {
    MipsDataService.getInstance().setActiveYear(year);
    setProgramYearState(year);
    clearOutcomeCache();
  };

  // Load the list of locked (read-only) performance years
  const refreshLockedYears = async () => {
    try {
      if (!localStorage.getItem('auth_token')) return;
      const response = await fetch(`${apiUrl}/api/year-management/locked-years`, { headers: authHeaders() });
      if (response.ok) {
        const data = await response.json();
        setLockedYears(Array.isArray(data) ? data.map((r: { program_year: number }) => r.program_year) : []);
      }
    } catch (error) {
      console.error('Error loading locked years:', error);
    }
  };

  // Load the years that should appear in the Viewing Year dropdown
  const refreshAvailableYears = async (operating: number) => {
    try {
      if (!localStorage.getItem('auth_token')) return;
      const response = await fetch(`${apiUrl}/api/system-preferences/available-years`, { headers: authHeaders() });
      if (response.ok) {
        const data = await response.json();
        const years: number[] = Array.isArray(data?.years) ? data.years : [];
        // Always include the operating year; sort descending; de-dupe.
        const set = new Set<number>(years);
        set.add(operating);
        setAvailableYears(Array.from(set).sort((a, b) => b - a));
      } else {
        setAvailableYears([operating]);
      }
    } catch (error) {
      console.error('Error loading available years:', error);
      setAvailableYears([operating]);
    }
  };

  // Load operating year from API; default the viewing year to it
  const loadProgramYear = async () => {
    try {
      if (!localStorage.getItem('auth_token')) {
        setLoading(false);
        return;
      }
      const response = await fetch(`${apiUrl}/api/system-preferences/program-year`, { headers: authHeaders() });
      const year = response.ok ? ((await response.json()).year || fallbackYear) : fallbackYear;
      MipsDataService.getInstance().setActiveYear(year);
      setOperatingYear(year);
      setProgramYearState(year); // viewing year starts on the operating year each session
      await refreshAvailableYears(year);
    } catch (error) {
      console.error('Error loading program year:', error);
      setOperatingYear(fallbackYear);
      setProgramYearState(fallbackYear);
    } finally {
      setLoading(false);
    }
  };

  // Admin: set the global operating year (and reset the viewing year to it)
  const updateProgramYear = async (year: number) => {
    const response = await fetch(`${apiUrl}/api/system-preferences/program-year`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ year }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to update program year');
    }
    MipsDataService.getInstance().setActiveYear(year);
    setOperatingYear(year);
    setProgramYearState(year);
    clearOutcomeCache();
    await refreshAvailableYears(year);
  };

  const refreshProgramYear = async () => {
    await loadProgramYear();
  };

  useEffect(() => {
    if (token) {
      setLoading(true);
      loadProgramYear();
      refreshLockedYears();
    } else {
      // Logged out / no token yet: don't sit in a perpetual loading state.
      setLoading(false);
    }
    // Re-run on auth change (login/logout/token-restore) so availableYears is
    // never stuck at the pre-auth fallback after a fresh SPA login.
  }, [token]);

  const isYearLocked = (year: number) => lockedYears.includes(year);

  const value: ProgramYearContextType = {
    programYear,
    operatingYear,
    availableYears,
    loading,
    setViewingYear,
    updateProgramYear,
    refreshProgramYear,
    lockedYears,
    isYearLocked,
    isCurrentYearLocked: lockedYears.includes(programYear),
    refreshLockedYears,
  };

  return (
    <ProgramYearContext.Provider value={value}>
      {children}
    </ProgramYearContext.Provider>
  );
}

export function useProgramYear() {
  const context = useContext(ProgramYearContext);
  if (context === undefined) {
    throw new Error('useProgramYear must be used within a ProgramYearProvider');
  }
  return context;
}
